import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Form-mode building blocks shared by the Org / Project / User create+edit forms,
 * ported from reference/crud_reference. List-mode primitives live in adminUi.tsx.
 */

type Entity = 'organizations' | 'projects' | 'users';

const ENTITY_TABS: { key: Entity; label: string; to: string }[] = [
  { key: 'organizations', label: 'Organization', to: '/organizations' },
  { key: 'projects', label: 'Project', to: '/projects' },
  { key: 'users', label: 'User', to: '/users' },
];

/** Full-screen form chrome: 60px top bar (brand + entity tabs + actions) + scrolling body. */
export function AdminFormShell({
  activeEntity,
  actions,
  children,
}: {
  activeEntity: Entity;
  actions: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="theme-noc"
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--surface-app)',
        color: 'var(--text-primary)',
        display: 'grid',
        gridTemplateRows: '60px 1fr',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 22,
          padding: '0 22px',
          background: 'var(--surface-raised)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Link
          to="/"
          className="mono"
          style={{ fontWeight: 700, fontSize: 14, letterSpacing: 2, color: 'var(--accent)', textDecoration: 'none' }}
        >
          FLYCOMMC2
        </Link>
        <nav style={{ display: 'flex', gap: 4 }}>
          {ENTITY_TABS.map((t) => {
            const active = t.key === activeEntity;
            return (
              <Link
                key={t.key}
                to={t.to}
                style={{
                  fontSize: 13,
                  padding: '6px 12px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  color: active ? 'var(--accent)' : 'var(--text-muted)',
                  background: active ? 'rgba(0,229,255,0.12)' : 'transparent',
                  border: `1px solid ${active ? 'rgba(0,229,255,0.5)' : 'transparent'}`,
                }}
              >
                {t.label}
              </Link>
            );
          })}
        </nav>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>{actions}</div>
      </div>
      <div className="scrl" style={{ overflowY: 'auto', minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

/** Two-column split: a form column + a map (or any) panel. Used by Org/Project. */
export function FormSplit({ form, panel }: { form: ReactNode; panel: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(480px,560px) 1fr', minHeight: 0, height: '100%' }}>
      <div className="scrl" style={{ overflowY: 'auto', padding: '28px 32px', minHeight: 0 }}>
        {form}
      </div>
      <div style={{ position: 'relative', borderLeft: '1px solid var(--border)', minHeight: 0 }}>{panel}</div>
    </div>
  );
}

/** Centered single column, for the User form. */
export function FormColumn({ children }: { children: ReactNode }) {
  return (
    <div className="scrl" style={{ overflowY: 'auto', height: '100%', padding: '28px 24px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

/** Eyebrow label (NEW ORGANIZATION) + page title + optional subtitle. */
export function FormHeader({
  eyebrow,
  title,
  subtitle,
  meta,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  meta?: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        className="mono"
        style={{ fontSize: 11, letterSpacing: 2, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 8 }}
      >
        {eyebrow}
      </div>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{title}</h1>
      {subtitle && (
        <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-dim)' }}>{subtitle}</p>
      )}
      {meta}
    </div>
  );
}

const sectionLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 2,
  color: 'var(--text-faint)',
  textTransform: 'uppercase',
  marginBottom: 14,
};

export function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 26 }}>
      <div style={sectionLabel}>{title}</div>
      {children}
    </section>
  );
}

export interface SegOption {
  value: string;
  label: string;
  desc?: string;
}

/**
 * Pill / card group. The parent owns selection state (single- or multi-select) —
 * this just renders and reports clicks. `variant="cards"` shows the description
 * column used by the User role selector.
 */
export function SegmentedControl({
  options,
  isActive,
  onClick,
  variant = 'row',
  disabled,
}: {
  options: SegOption[];
  isActive: (value: string) => boolean;
  onClick: (value: string) => void;
  variant?: 'row' | 'cards';
  disabled?: boolean;
}) {
  const cards = variant === 'cards';
  return (
    <div style={{ display: 'flex', flexDirection: cards ? 'column' : 'row', gap: 8, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = isActive(o.value);
        return (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => onClick(o.value)}
            style={{
              flex: cards ? '1 1 auto' : '0 0 auto',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: cards ? '12px 14px' : '9px 15px',
              borderRadius: cards ? 11 : 8,
              cursor: disabled ? 'default' : 'pointer',
              transition: 'all .15s',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--accent)' : 'var(--text-muted)',
              background: active ? 'rgba(0,229,255,0.12)' : 'var(--surface-card)',
              border: `1px solid ${active ? 'rgba(0,229,255,0.5)' : 'var(--border)'}`,
              opacity: disabled ? 0.7 : 1,
            }}
          >
            <span>{o.label}</span>
            {o.desc && <span style={{ fontSize: 11.5, color: 'var(--text-faint)', fontWeight: 400 }}>{o.desc}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Mono ID / created / updated row, shown only in edit mode. */
export function RecordMeta({ id, createdAt }: { id: string; createdAt?: string }) {
  return (
    <div
      className="mono"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, fontSize: 11, color: 'var(--text-faint)' }}
    >
      <span>ID {id}</span>
      {createdAt && <span>CREATED {new Date(createdAt).toISOString().slice(0, 10)}</span>}
    </div>
  );
}

/** "1,284 km²" — locale-grouped area. */
export function formatAreaKmSq(n: number): string {
  return `${Math.round(n).toLocaleString()} km²`;
}

/** Subtle top-bar button (Cancel). */
export const secondaryBtnStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text-secondary)',
  fontSize: 13,
  padding: '8px 16px',
  cursor: 'pointer',
  textDecoration: 'none',
};

/** Destructive top-bar button (Delete / Remove). */
export const dangerBtnStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255,93,115,0.5)',
  borderRadius: 8,
  color: '#ff5d73',
  fontSize: 13,
  padding: '8px 16px',
  cursor: 'pointer',
};
